// Global modal manager to prevent focus trap conflicts
export class ModalManager {
  constructor() {
    this.activeModals = new Map();
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized || !window.bootstrap) return;
    
    // Override Bootstrap Modal constructor to track instances
    const OriginalModal = window.bootstrap.Modal;
    const manager = this;
    
    window.bootstrap.Modal = function(element, config) {
      const instance = new OriginalModal(element, config);
      manager.trackModal(element, instance);
      return instance;
    };
    
    // Copy static methods
    Object.setPrototypeOf(window.bootstrap.Modal, OriginalModal);
    Object.setPrototypeOf(window.bootstrap.Modal.prototype, OriginalModal.prototype);
    
    // Override getOrCreateInstance to ensure proper tracking
    const originalGetOrCreate = OriginalModal.getOrCreateInstance;
    window.bootstrap.Modal.getOrCreateInstance = function(element, config) {
      const instance = originalGetOrCreate.call(OriginalModal, element, config);
      manager.trackModal(element, instance);
      return instance;
    };
    
    // Copy other static methods
    window.bootstrap.Modal.getInstance = OriginalModal.getInstance;
    window.bootstrap.Modal.VERSION = OriginalModal.VERSION;
    window.bootstrap.Modal.NAME = OriginalModal.NAME;
    window.bootstrap.Modal.DATA_KEY = OriginalModal.DATA_KEY;
    window.bootstrap.Modal.EVENT_KEY = OriginalModal.EVENT_KEY;
    window.bootstrap.Modal.Default = OriginalModal.Default;
    
    this.initialized = true;
    
    // Clean up on Turbo navigation
    document.addEventListener('turbo:before-cache', () => this.cleanupAllModals());
    document.addEventListener('turbo:before-render', () => this.cleanupAllModals());
  }
  
  trackModal(element, instance) {
    if (!this.activeModals.has(element)) {
      this.activeModals.set(element, instance);
      
      // Listen for disposal
      const originalDispose = instance.dispose.bind(instance);
      instance.dispose = () => {
        this.activeModals.delete(element);
        originalDispose();
      };
    }
  }
  
  cleanupAllModals() {
    // Dispose of all tracked modal instances
    this.activeModals.forEach((instance, element) => {
      try {
        // Hide modal if it's shown
        if (element.classList.contains('show')) {
          instance.hide();
        }
        // Dispose of the instance
        instance.dispose();
      } catch (error) {
        console.warn('Error cleaning up modal:', error);
      }
    });
    
    // Clear the map
    this.activeModals.clear();
    
    // Clean up any stray backdrops
    document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
    
    // Remove modal-open class from body
    document.body.classList.remove('modal-open');
    
    // Reset body styles that might have been added
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }
  
  // Get all active modal instances
  getActiveModals() {
    return Array.from(this.activeModals.values());
  }
  
  // Check if any modals are currently open
  hasOpenModals() {
    return Array.from(this.activeModals.keys()).some(element => 
      element.classList.contains('show')
    );
  }
}

// Create and export singleton instance
const modalManager = new ModalManager();
export default modalManager;